<template>
  <VueFinalModal
    v-model="localShowModal"
    classes="flex justify-end"
    content-class="modal-content"
    :click-to-close="true"
    @closed="emit('closeModal')"
  >
    <header class="modal-header">
      <h2>{{ selectedPlan.plan_name }} Plan</h2>
      <i
        @click="close"
        aria-label="close icon"
        class="emb_closeicon close-btn"
      ></i>
    </header>

    <!-- Tabs -->
    <nav class="modal-tabs">
      <button
        :class="['tab-btn', { active: tab === 'exterior' }]"
        @click="tab = 'exterior'"
      >
        Exterior Options
      </button>
      <button
        v-if="false"
        :class="[
          'tab-btn',
          { active: tab === 'design', disabled: !selectedLotInfo?.id },
        ]"
        @click="handleDesignTabClick"
      >
        Design Options
      </button>
    </nav>

    <section class="modal-body scrollbar">
      <ExteriorOptions
        v-if="tab === 'exterior'"
        :elevations="elevations"
        :selectedPlan="selectedPlan"
        :selected="selected"
        :loading="loadingElevations"
        :error="elevationsError"
        :planName="planName"
        :hasAvailableLots="hasAvailableLots"
        :planPriceLowWithPremium="selectedPlan?.price_low_with_premium"
        @update:selected="selected = $event"
      />

      <DesignOptions
        v-else-if="tab === 'design'"
        :designOptions="designOptions"
        :selected="selected"
        @update:selected="selected = $event"
      />

      <div v-else class="empty-tab">
        <p>No items available.</p>
      </div>
    </section>
  </VueFinalModal>
</template>

<script setup lang="ts">
import { ref, toRefs, watch, onMounted } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import { ElevationResponse, Elevation } from '@/types/elevations';
import ExteriorOptions from '../ExteriorOptions.vue';
import DesignOptions from '../DesignOptions.vue';
import { apiFetchElevationDetails } from '@/api/elevation-api';
import { Lot } from '@/types';

const props = defineProps<{
  showModal: boolean;
  selectedPlan: any;
  areaId: string;
  selectedLotInfo?: Lot;
  hasAvailableLots: boolean;
}>();
const { showModal, selectedPlan, areaId, selectedLotInfo, hasAvailableLots } = toRefs(props);

const emit = defineEmits(['update:showModal', 'closeModal']);

const localShowModal = ref(props.showModal);

watch(
  () => props.showModal,
  (val) => (localShowModal.value = val)
);
watch(localShowModal, (val) => emit('update:showModal', val));

const tab = ref<'exterior' | 'design'>('exterior');
const selected = ref<number | null>(0);

const elevations = ref<Elevation[]>([]);
const planName = ref('');
const loadingElevations = ref(false);
const elevationsError = ref<string | null>(null);

const designOptions = ref([
  {
    title: 'Brick',
    description: 'Auberdean',
    img: 'https://assetcloud.dreeshomes.com/transform/e1aa94cd-3aaa-4754-bd89-1ebb1a4fbbfb/Aiden_Kitchen-jpg?scale=small&io=transform%3Afill%2Cwidth%3A501',
  },
  {
    title: 'Mortar Color',
    description: 'Buff',
    img: 'https://assetcloud.dreeshomes.com/transform/e1aa94cd-3aaa-4754-bd89-1ebb1a4fbbfb/Aiden_Kitchen-jpg?scale=small&io=transform%3Afill%2Cwidth%3A501',
  },
  {
    title: 'Mortar Finish',
    description: 'Tooled and Tiled',
    img: 'https://assetcloud.dreeshomes.com/transform/e1aa94cd-3aaa-4754-bd89-1ebb1a4fbbfb/Aiden_Kitchen-jpg?scale=small&io=transform%3Afill%2Cwidth%3A501',
  },
]);

const handleDesignTabClick = () => {
  if (selectedLotInfo.value) {
    tab.value = 'design';
  }
};

const fetchElevations = async () => {
  loadingElevations.value = true;
  elevationsError.value = null;
  try {
    const response = await apiFetchElevationDetails(
      areaId.value,
      selectedPlan.value.neighborhood_code,
      selectedPlan.value.plan_id
    );
    elevations.value = response?.elevations || [];
    planName.value = response.plan_name;
  } catch (err) {
    elevationsError.value = 'Failed to load elevations.';
  } finally {
    loadingElevations.value = false;
  }
};

watch(
  selectedPlan,
  () => {
    if (
      selectedPlan.value.plan_id &&
      selectedPlan.value.neighborhood_code &&
      areaId.value
    ) {
      fetchElevations();
    }
  },
  { immediate: true, deep: true }
);
watch(showModal, (newVal) => {
  if (newVal) {
    tab.value = 'exterior';
  }
});
const close = () => {
  emit('closeModal');
};
</script>

<style lang="scss">
.vfm__overlay {
  background: transparent !important;
}
.modal-content {
  position: fixed;
  top: 12px;
  left: 390px;
  width: 272px;
  border-radius: 8px;
  max-height: calc(100% - 24px);
  padding: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  font-family: sans-serif;
  z-index: 9999;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 9px;

  h2 {
    font-family: 'Open Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    color: $black;
  }
}

.modal-tabs {
  display: flex;
  border-bottom: 1px solid #e0e0e0;

  .tab-btn {
    flex: 1;
    padding: 10px 6px;
    font-family: 'Open Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    text-align: center;
    background: none;
    border: none;
    cursor: pointer;
    color: #777;

    &.active {
      font-weight: 600;
      border-bottom: 2px solid $lime-green-new;
      color: $gray-700;
    }
    &.disabled {
      color: #777;
      pointer-events: none;
      opacity: 0.5;
    }
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  display: block;
  padding-right: 4px;
}
.plan-card:not(.design-option-card) {
  margin-top: 12px;
}
.design-option-card-group {
  margin-top: 12px;
}
.design-option-card {
  &-title {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 6px;
    color: $black;
  }
  &-dis {
    font-weight: 400 !important;
  }
}
.plan-card {
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  transition: background-color 0.2s, border-color 0.2s;
  border: 2px solid $gray-100;

  &.selected {
    background-color: var(--cardBg, $white);
    --cardBg: var(--commCardBgSelected);
    border-color: $lime-green-new;
  }

  img {
    width: 100%;
    max-height: 180px;
    object-fit: cover;
    display: block;
  }

  .plan-info {
    padding: 8px 10px;

    h3 {
      font-size: 14px;
      margin: 0 0 4px;
      font-weight: 400;
      color: $gray-700;
    }

    p {
      margin: 0;
      font-size: 12px;
      font-weight: 600;
      color: $gray-700;
      word-wrap: break-word;
    }
    &-label {
      font-weight: 400;
      color: $gray-300;
    }
  }
}
</style>
